'use client'

import { useState, useEffect } from 'react'
import { Plus, Grid3X3, Calendar, Info, FileText } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useTimezone } from '@/hooks/useTimezone'
import { Button } from '@/components/ui/button'
import { Skeleton } from "@/components/ui/skeleton"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { 
  useContentData, 
  Content
} from './useContentData'
import { supabase } from '@/lib/supabase'
import { useBrands } from '@/lib/BrandsProvider'
import NoBrandSelected from '@/components/NoBrandSelected'
import { ChangeScheduleModal } from './components/ChangeScheduleModal'
import { TimezoneContext } from '@/components/timezone'
import { ContentFilters } from './components/ContentFilters'
import { ContentSelectionToolbar } from './components/ContentSelectionToolbar'
import { ContentCardView } from './components/ContentCardView'
import { ContentCalendarView } from './components/ContentCalendarView'
import {
  useContentFilters,
  useContentSelection,
  useContentDragDrop
} from './hooks'
import { CalendarDateType } from './types/contentManagement'
import { loadContentPreferences, saveContentPreferences } from './utils/contentPreferences'

// Pagination constants
const ITEMS_PER_PAGE = 12;

export default function ContentManagement() {
  const [contents, setContents] = useState<Content[]>([])
  const [loading, setLoading] = useState(true)

  // Load saved preferences on mount
  const savedPreferences = loadContentPreferences()
  const [viewMode, setViewMode] = useState<'cards' | 'calendar'>(savedPreferences.viewMode)
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date())
  const [filtersExpanded, setFiltersExpanded] = useState(false)
  const [calendarDateType, setCalendarDateType] = useState<CalendarDateType>(savedPreferences.calendarDateType)
  
  const [changeScheduleModal, setChangeScheduleModal] = useState<{
    isOpen: boolean;
    contentId: string;
    currentScheduledTime?: string | null;
  }>({ isOpen: false, contentId: '', currentScheduledTime: null })
  
  const navigate = useNavigate()
  const { fetchContentsByBrand } = useContentData()
  const { selectedBrand } = useBrands()
  const { formatDate: formatDateInUserTimezone } = useTimezone()
  
  // Use hooks for content management
  const contentFilters = useContentFilters({
    contents,
    itemsPerPage: ITEMS_PER_PAGE,
    initialPreferences: savedPreferences
  })
  
  const contentSelection = useContentSelection({
    contents,
    paginatedContents: contentFilters.paginatedContents,
    onRefresh: loadContents
  })
  
  const contentDragDrop = useContentDragDrop({
    onRefresh: loadContents,
    currentMonth
  })

  // Save view mode preference when it changes
  useEffect(() => {
    saveContentPreferences({ viewMode });
  }, [viewMode]);

  // Save calendar date type preference when it changes
  useEffect(() => {
    saveContentPreferences({ calendarDateType });
  }, [calendarDateType]);

  // Handle platform icon click - navigate to platform connection settings
  const handlePlatformIconClick = (e: React.MouseEvent, platformKey: string | null) => {
    e.stopPropagation();
    if (!selectedBrand?.id || !platformKey) return;

    // Navigate to platform connections page with the specific platform
    navigate(`/brands/${selectedBrand.id}/connections`, {
      state: { highlightPlatform: platformKey }
    });
  };

  useEffect(() => {
    if (selectedBrand?.id) {
      loadContents()
    }

    // Set up realtime subscription for content changes using the new view
    const subscription = supabase
      .channel('content_changes')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'content_management_view',
        filter: selectedBrand?.id ? `brand_id=eq.${selectedBrand.id}` : undefined
      }, payload => {
        console.log('Realtime update from content_management_view:', payload);
        if (payload.eventType === 'INSERT') {
          // Transform the payload to match our Content type
          const newContent = transformViewDataToContent(payload.new);
          if (newContent) {
            setContents(current => [...current, newContent])
          }
        } else if (payload.eventType === 'DELETE') {
          setContents(current => current.filter(content => content.id !== payload.old.id))
        } else if (payload.eventType === 'UPDATE') {
          const updatedContent = transformViewDataToContent(payload.new);
          if (updatedContent) {
            setContents(current => current.map(content => 
              content.id === updatedContent.id ? updatedContent : content
            ))
          }
        }
      })
      .subscribe()

    return () => {
      subscription.unsubscribe()
    }
  }, [selectedBrand?.id])

  // Transform view data to Content format (keeping type compatibility)
  const transformViewDataToContent = (viewData: any): Content | null => {
    try {
      return {
        ...viewData,
        campaigns: viewData.campaign_name ? {
          id: viewData.campaign_id,
          name: viewData.campaign_name
        } : null
      } as Content;
    } catch (error) {
      console.error('Error transforming view data:', error);
      return null;
    }
  };

  async function loadContents() {
    if (!selectedBrand?.id) return
    
    try {
      setLoading(true)
      console.log('Loading contents for brand:', selectedBrand.id)
      
      const fetchedContents = await fetchContentsByBrand(selectedBrand.id)
      console.log('Fetched contents:', fetchedContents?.length)
      
      setContents(fetchedContents || [])
    } catch (error) {
      console.error('Error loading contents:', error)
    } finally {
      setLoading(false)
    }
  }

  // Utility functions for content management
  const getContentImage = (content: Content): string | null => {
    try {
      // First check the new direct image_url field for better performance
      if (content.image_url) {
        return content.image_url;
      }

      // Fallback to parsing content_json for backward compatibility
      if (content.content_json?.image_url) {
        return content.content_json.image_url;
      }
      // Check for social post structure (LinkedIn, Twitter, etc.)
      if (content.content_json?.posts && Array.isArray(content.content_json.posts) && content.content_json.posts.length > 0) {
        const firstPost = content.content_json.posts[0];
        if (firstPost.image_url) {
          return firstPost.image_url;
        }
        // Also check for images array in the post
        if (firstPost.images && Array.isArray(firstPost.images) && firstPost.images.length > 0) {
          return firstPost.images[0];
        }
      }
      if (content.content_json?.images && Array.isArray(content.content_json.images) && content.content_json.images.length > 0) {
        return content.content_json.images[0];
      }
      if (content.content_json?.media && Array.isArray(content.content_json.media)) {
        const imageMedia = content.content_json.media.find((m: any) => m.type === 'image');
        if (imageMedia?.url) return imageMedia.url;
      }
    } catch (error) {
      console.error('Error extracting image from content:', error);
    }
    return null;
  };
  
  const hasVideo = (content: Content): boolean => {
    try {
      // First check the new direct video_url field for better performance
      if (content.video_url) return true;

      // Fallback to parsing content_json for backward compatibility
      if (content.content_json?.video_url) return true;
      // Check for social post structure
      if (content.content_json?.posts && Array.isArray(content.content_json.posts) && content.content_json.posts.length > 0) {
        const firstPost = content.content_json.posts[0];
        if (firstPost.video_url) return true;
        if (firstPost.videos && Array.isArray(firstPost.videos) && firstPost.videos.length > 0) {
          return true;
        }
      }
      if (content.content_json?.media && Array.isArray(content.content_json.media)) {
        return content.content_json.media.some((m: any) => m.type === 'video');
      }
    } catch (error) {
      console.error('Error checking for video in content:', error);
    }
    return false;
  };

  const getTextPreview = (content: Content): string | null => {
    try {
      // First check the new direct text_preview field for better performance
      if (content.text_preview) {
        return content.text_preview;
      }

      // Fallback to extracting from content_json for backward compatibility
      if (content.content_json) {
        // Try to extract meaningful text content from various structures
        if (content.content_json.content) {
          // Strip HTML tags and limit length
          const textContent = content.content_json.content.replace(/<[^>]*>/g, '').trim();
          if (textContent) {
            return textContent.length > 300 ? textContent.substring(0, 297) + '...' : textContent;
          }
        }

        if (content.content_json.caption) {
          return content.content_json.caption.length > 300 ? content.content_json.caption.substring(0, 297) + '...' : content.content_json.caption;
        }

        if (content.content_json.text) {
          return content.content_json.text.length > 300 ? content.content_json.text.substring(0, 297) + '...' : content.content_json.text;
        }

        // Check for social post structure
        if (content.content_json.posts && Array.isArray(content.content_json.posts) && content.content_json.posts.length > 0) {
          const firstPost = content.content_json.posts[0];
          if (firstPost.text) {
            return firstPost.text.length > 300 ? firstPost.text.substring(0, 297) + '...' : firstPost.text;
          }
          if (firstPost.caption) {
            return firstPost.caption.length > 300 ? firstPost.caption.substring(0, 297) + '...' : firstPost.caption;
          }
        }
      }
    } catch (error) {
      console.error('Error extracting text preview from content:', error);
    }
    return null;
  };

  if (!selectedBrand) {
    return <NoBrandSelected />
  }

  // Event handlers
  const handleCreateContent = () => {
    navigate('/content/create')
  }

  const handleViewEdit = (contentId: string) => {
    navigate(`/content/${contentId}`)
  }

  const handleChangeSchedule = (content: Content) => {
    setChangeScheduleModal({
      isOpen: true,
      contentId: content.id,
      currentScheduledTime: content.scheduled_time
    })
  }

  const handleScheduleModalClose = () => {
    setChangeScheduleModal({ isOpen: false, contentId: '', currentScheduledTime: null })
  }

  const handleScheduleUpdate = () => {
    // Refresh content data after schedule update
    loadContents()
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-5" />
        <div className="relative container mx-auto p-6 max-w-7xl space-y-8">
          
          {/* Header Skeleton */}
          <div className="relative overflow-hidden bg-gradient-to-br from-white via-blue-50/30 to-indigo-50/40 rounded-2xl border border-blue-200/50 shadow-sm p-4 md:p-8">
            <div className="absolute inset-0 bg-grid-pattern opacity-5" />
            <div className="relative flex flex-col lg:flex-row lg:items-center justify-between gap-4 md:gap-6">
              <div className="flex items-center gap-2 md:gap-4 flex-1">
                <Skeleton className="h-10 w-10 md:h-12 md:w-12 rounded-xl md:rounded-2xl" />
                <div className="space-y-2 flex-1">
                  <Skeleton className="h-8 md:h-10 w-48 md:w-64" />
                  <Skeleton className="h-4 md:h-6 w-full max-w-2xl hidden md:block" />
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <Skeleton className="h-12 w-40" />
              </div>
            </div>
          </div>

          {/* Filters Skeleton */}
          <div className="relative overflow-hidden bg-white/60 backdrop-blur-sm rounded-xl border border-white/50 shadow-sm p-6">
            <div className="flex gap-4">
              <Skeleton className="h-12 flex-grow" />
              <Skeleton className="h-12 w-40" />
              <Skeleton className="h-12 w-40" />
              <Skeleton className="h-12 w-40" />
            </div>
          </div>

          {/* Content Grid Skeleton */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="bg-white/60 backdrop-blur-sm rounded-2xl border border-white/50 shadow-sm overflow-hidden">
                <Skeleton className="h-48 w-full" />
                <div className="p-6 space-y-4">
                  <Skeleton className="h-6 w-full" />
                  <Skeleton className="h-4 w-24" />
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-3/4" />
                  </div>
                  <Skeleton className="h-10 w-full" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30 relative overflow-hidden">
      <div className="absolute inset-0 bg-grid-pattern opacity-5" />
      <div className="relative container mx-auto p-6 max-w-7xl space-y-8">
        {/* Header */}
        <div className="relative overflow-hidden bg-gradient-to-br from-white via-blue-50/30 to-indigo-50/40 rounded-2xl border border-blue-200/50 shadow-sm p-4 md:p-8">
          <div className="absolute inset-0 bg-grid-pattern opacity-5" />
          <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-blue-200/20 to-transparent rounded-full -translate-y-20 translate-x-20" />
          <div className="relative">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 md:gap-6">
              <div className="flex items-center gap-2 md:gap-4 flex-1">
                <div className="p-2 md:p-3 bg-white/80 backdrop-blur-sm rounded-xl md:rounded-2xl shadow-lg">
                  <FileText className="h-5 w-5 md:h-8 md:w-8 text-blue-600" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1 md:mb-2">
                    <h1 className="text-2xl md:text-4xl font-bold bg-gradient-to-r from-blue-700 via-indigo-700 to-purple-700 bg-clip-text text-transparent">
                      Content Management
                    </h1>
                    {/* Tooltip icon - visible on all screen sizes */}
                    <TooltipProvider delayDuration={200}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button 
                            type="button"
                            className="p-1.5 rounded-full hover:bg-blue-100/50 transition-colors flex-shrink-0"
                            aria-label="Content Management information"
                          >
                            <Info className="h-4 w-4 md:h-5 md:w-5 text-blue-600" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" className="max-w-xs p-3">
                          <p className="text-sm leading-relaxed">
                            Create, schedule, and publish content across all your social platforms. Streamline your content workflow with intelligent scheduling and cross-platform publishing.
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  {/* Desktop-only description text - mobile users see tooltip instead */}
                  <p className="text-gray-600 text-base md:text-lg leading-relaxed max-w-2xl hidden md:block">
                    Create, schedule, and publish content across all your social platforms. Streamline your content workflow with intelligent scheduling and cross-platform publishing.
                  </p>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-3">
                <Button 
                  onClick={handleCreateContent}
                  className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-sm hover:shadow-lg transition-all duration-300 transform hover:scale-105 border-0"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create Content
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Timezone Context - Hidden on mobile screens */}
        <div className="hidden sm:block">
          <TimezoneContext 
            placement="inline" 
            compact={true}
            className="mb-4 bg-white/60 backdrop-blur-sm border border-white/50 shadow-sm"
          />
        </div>

        {/* Bulk Actions Bar */}
        <ContentSelectionToolbar
          selectedContent={contentSelection.selectedContent}
          isSelectionMode={contentSelection.isSelectionMode}
          contents={contents}
          paginatedContents={contentFilters.paginatedContents}
          onToggleSelectAll={contentSelection.toggleSelectAll}
          onBulkCancelScheduled={contentSelection.bulkCancelScheduled}
          // Add other bulk operations when they're implemented
        />

        {/* View Mode Toggle */}
        <div className="mb-4">
          <div className="flex justify-center">
            <div className="relative overflow-hidden bg-white/60 backdrop-blur-sm rounded-lg border border-white/50 shadow-sm">
              <div className="flex">
                <Button
                  variant={viewMode === 'cards' ? 'default' : 'ghost'}
                  size="sm"
                  className={`rounded-l-lg rounded-r-none h-10 px-4 text-sm ${
                    viewMode === 'cards'
                      ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-sm'
                      : 'bg-transparent hover:bg-white/60'
                  } transition-all duration-300`}
                  onClick={() => setViewMode('cards')}
                >
                  <Grid3X3 className="h-4 w-4 mr-2" />
                  Cards
                </Button>
                <Button
                  variant={viewMode === 'calendar' ? 'default' : 'ghost'}
                  size="sm"
                  className={`rounded-r-lg rounded-l-none h-10 px-4 text-sm ${
                    viewMode === 'calendar'
                      ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-sm'
                      : 'bg-transparent hover:bg-white/60'
                  } transition-all duration-300`}
                  onClick={() => setViewMode('calendar')}
                >
                  <Calendar className="h-4 w-4 mr-2" />
                  Calendar
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Filters Section - applies to both views */}
        <ContentFilters
          search={contentFilters.search}
          onSearchChange={contentFilters.setSearch}
          contentType={contentFilters.contentType}
          onContentTypeChange={contentFilters.setContentType}
          statusFilter={contentFilters.statusFilter}
          onStatusFilterChange={contentFilters.setStatusFilter}
          platformFilter={contentFilters.platformFilter}
          onPlatformFilterChange={contentFilters.setPlatformFilter}
          sortConfig={contentFilters.sortConfig}
          onSortConfigChange={contentFilters.setSortConfig}
          filtersExpanded={filtersExpanded}
          onFiltersExpandedChange={setFiltersExpanded}
          viewMode={viewMode}
        />

        {/* Content Display Area */}
        {viewMode === 'cards' ? (
          <ContentCardView
            paginatedContents={contentFilters.paginatedContents}
            filteredContents={contentFilters.filteredContents}
            currentPage={contentFilters.currentPage}
            totalPages={contentFilters.totalPages}
            onPageChange={contentFilters.setCurrentPage}
            selectedContent={contentSelection.selectedContent}
            isSelectionMode={contentSelection.isSelectionMode}
            onToggleContentSelection={contentSelection.toggleContentSelection}
            onViewEdit={handleViewEdit}
            onChangeSchedule={handleChangeSchedule}
            onCreateContent={handleCreateContent}
            onPlatformIconClick={handlePlatformIconClick}
            getContentImage={getContentImage}
            hasVideo={hasVideo}
            getTextPreview={getTextPreview}
          />
        ) : (
          <div className="relative overflow-hidden bg-white/60 backdrop-blur-sm rounded-xl border border-white/50 shadow-sm">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-50/20 via-transparent to-purple-50/20" />
            <div className="relative">
              <ContentCalendarView 
                contents={contentFilters.sortedContents} 
                currentMonth={currentMonth}
                onCurrentMonthChange={setCurrentMonth}
                dateType={calendarDateType}
                onDateTypeChange={setCalendarDateType}
                dragOverDay={contentDragDrop.dragOverDay}
                onDragStart={contentDragDrop.handleDragStart}
                onDragEnd={contentDragDrop.handleDragEnd}
                onDragOver={contentDragDrop.handleDragOver}
                onDragLeave={contentDragDrop.handleDragLeave}
                onDrop={contentDragDrop.handleDrop}
                isContentDraggable={contentDragDrop.isContentDraggable}
                onViewEdit={handleViewEdit}
                onPlatformIconClick={handlePlatformIconClick}
                formatDateInUserTimezone={formatDateInUserTimezone}
              />
            </div>
          </div>
        )}
      </div>

      {/* Change Schedule Modal */}
      <ChangeScheduleModal
        isOpen={changeScheduleModal.isOpen}
        onClose={handleScheduleModalClose}
        contentId={changeScheduleModal.contentId}
        currentScheduledTime={changeScheduleModal.currentScheduledTime}
        onScheduleUpdate={handleScheduleUpdate}
      />
    </div>
  )
}